import { API_BASE } from '../config';

export type Track = {
  id: string;
  title: string;
  artist: string;
  filename: string;
  cover_filename?: string | null;
  album?: string | null;
  audio_version?: number;
  play_count?: number;
};

export type ArtistBrowse = {
  artist: string;
  uploader_id?: string;
  track_count: number;
  latest_release?: string;
  cover_filename?: string | null;
  slug?: string;
  registered?: boolean;
};

export type SearchResult = {
  type: string;
  id: string;
  title: string;
  artist?: string;
  album?: string | null;
  cover_filename?: string | null;
  filename?: string;
  badge?: string;
};

export type AuthMe = {
  authenticated: boolean;
  user?: {
    discord_id: string;
    username: string;
    display_name?: string | null;
    avatar?: string | null;
  };
};

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { Accept: 'application/json' },
    cache: 'no-store',
  });
  if (!res.ok) {
    throw new Error(`${path} → ${res.status}`);
  }
  return res.json() as Promise<T>;
}

/** Search hits often omit `filename`; FreeMusiers stores audio as `{id}.mp3`. */
function trackFromSearch(r: SearchResult, artistFallback = 'Unknown'): Track | null {
  if (r.type !== 'song' || !r.id) return null;
  const filename = r.filename?.trim() || `${r.id}.mp3`;
  return {
    id: r.id,
    title: r.title || 'Untitled',
    artist: r.artist || artistFallback,
    filename,
    cover_filename: r.cover_filename ?? null,
    album: r.album ?? null,
  };
}

export function trackAudioUrl(track: Pick<Track, 'filename' | 'audio_version'>): string {
  const v = track.audio_version ?? 1;
  return `${API_BASE}/music/${encodeURIComponent(track.filename)}?v=${v}`;
}

export function trackCoverUrl(cover?: string | null): string | null {
  if (!cover) return null;
  // FreeMusiers serves artwork under /art/, not /music/
  return `${API_BASE}/art/${encodeURIComponent(cover)}`;
}

export async function fetchTracksPage(page = 1, limit = 24): Promise<Track[]> {
  const data = await getJson<{ tracks?: Track[] } | Track[]>(
    `/api/tracks?page=${page}&limit=${limit}`,
  );
  if (Array.isArray(data)) return data;
  return data.tracks ?? [];
}

export async function fetchNewest(limit = 24): Promise<Track[]> {
  const data = await getJson<{ results: SearchResult[] }>(
    `/api/search?category=new&bucket=7d&limit=${limit}`,
  );
  return data.results
    .map((r) => trackFromSearch(r))
    .filter((t): t is Track => t !== null);
}

export async function searchSongs(q: string, limit = 30): Promise<Track[]> {
  const data = await getJson<{ results: SearchResult[] }>(
    `/api/search?q=${encodeURIComponent(q)}&category=song&limit=${limit}`,
  );
  return data.results
    .map((r) => trackFromSearch(r))
    .filter((t): t is Track => t !== null);
}

export async function fetchArtists(limit = 200, offset = 0): Promise<ArtistBrowse[]> {
  // One endpoint: full catalog from track tags. (/api/artists = registered personas only.)
  const pageSize = 100;
  const out: ArtistBrowse[] = [];
  let page = 1;
  let hasNext = true;

  while (hasNext && out.length < offset + limit) {
    const data = await getJson<{
      results: Array<{
        type: string;
        artist?: string;
        uploader_id?: string;
        track_count?: number;
        cover_filename?: string | null;
      }>;
      has_next?: boolean;
    }>(`/api/search?category=artist&limit=${pageSize}&page=${page}`);

    for (const r of data.results) {
      if (r.type !== 'artist' || !r.artist) continue;
      out.push({
        artist: r.artist,
        uploader_id: r.uploader_id,
        track_count: r.track_count ?? 0,
        // Search artist hits do not include covers — UI uses initials avatar.
        cover_filename: r.cover_filename ?? null,
      });
    }
    hasNext = Boolean(data.has_next);
    page += 1;
    if (page > 30) break;
  }

  out.sort((a, b) => (b.track_count || 0) - (a.track_count || 0));
  return out.slice(offset, offset + limit);
}

export async function fetchArtistTracksBySlug(slug: string): Promise<Track[]> {
  const data = await getJson<{
    tracks?: Array<{
      id: string;
      title: string;
      artist?: string;
      filename?: string;
      cover_filename?: string | null;
      album?: string | null;
    }>;
    canonical_artist?: string;
    track_count?: number;
  }>(`/api/artists/${encodeURIComponent(slug)}`);

  const artistName = data.canonical_artist || 'Unknown';
  return (data.tracks ?? []).map((t) => ({
    id: t.id,
    title: t.title || 'Untitled',
    artist: t.artist || artistName,
    filename: t.filename?.trim() || `${t.id}.mp3`,
    cover_filename: t.cover_filename ?? null,
    album: t.album ?? null,
  }));
}

/**
 * Full discography via song search with artist: field filter.
 * (Plain q=Name searches titles — wrong and tiny.)
 */
export async function fetchArtistTracks(opts: {
  artist: string;
  slug?: string | null;
  limit?: number;
}): Promise<Track[]> {
  // Prefer field search — reliable full catalog. Slug profile is optional shortcut.
  const viaSearch = await searchByArtistName(opts.artist, opts.limit ?? 200);
  if (viaSearch.length) return viaSearch;

  const slug = opts.slug?.trim();
  if (slug) {
    try {
      return await fetchArtistTracksBySlug(slug);
    } catch {
      /* ignore */
    }
  }
  return [];
}

export async function searchByArtistName(artist: string, limit = 200): Promise<Track[]> {
  // FreeMusiers search: "artist:Name" sets field_filter=artist (title match is the default).
  const pageSize = 100;
  const out: Track[] = [];
  const seen = new Set<string>();
  let page = 1;
  let hasNext = true;
  const q = `artist:${artist}`;

  while (hasNext && out.length < limit) {
    const data = await getJson<{
      results: SearchResult[];
      has_next?: boolean;
    }>(
      `/api/search?q=${encodeURIComponent(q)}&category=song&limit=${pageSize}&page=${page}`,
    );
    for (const r of data.results) {
      if (r.type !== 'song') continue;
      const t = trackFromSearch(r, artist);
      if (!t || seen.has(t.id)) continue;
      seen.add(t.id);
      out.push(t);
    }
    hasNext = Boolean(data.has_next);
    page += 1;
    if (page > 30) break;
  }
  return out.slice(0, limit);
}

export async function fetchRandomTrack(): Promise<Track> {
  // Prefer dashboard random bucket when available; fall back to first page shuffle.
  try {
    const dash = await getJson<{ random?: Track[] }>('/api/tracks/dashboard');
    const pool = (dash.random ?? []).filter((t) => t?.filename);
    if (pool.length) {
      return pool[Math.floor(Math.random() * pool.length)];
    }
  } catch {
    /* ignore */
  }
  const page = await fetchTracksPage(1, 40);
  const usable = page.filter((t) => t?.filename);
  if (!usable.length) throw new Error('No tracks available');
  return usable[Math.floor(Math.random() * usable.length)];
}

export async function fetchTrack(id: string): Promise<Track> {
  const data = await getJson<{ track?: Track } & Track>(`/api/tracks/${encodeURIComponent(id)}`);
  if (data.track) return data.track;
  if (data.id && data.filename) return data as Track;
  throw new Error(`Track ${id} not found`);
}

export async function fetchMe(): Promise<AuthMe> {
  // Use no-cache to always get fresh auth state
  const res = await fetch(`${API_BASE}/api/auth/me`, {
    headers: { Accept: 'application/json' },
    cache: 'no-store',
  });
  if (!res.ok) {
    return { authenticated: false };
  }
  return res.json();
}
